import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { Picture } from '@/components/wpl/image/src/image';
import { Scroller, ScrollerArray } from '@/patterns/adg4/scroller/src/scroller';

const SideBySide = () => {
	const intl = useIntl();

	const items: ScrollerArray<ScrollerItem> = [
		{
			title: intl.formatMessage({
				id: 'JiraProductDiscoveryPage.scroller.1.title',
				defaultMessage: 'Bring structure to chaos',
			}),
			description: intl.formatMessage({
				id: 'JiraProductDiscoveryPage.scroller.1.description',
				defaultMessage:
					'Leverage data and customer insights to help your team prioritize ideas that will make the biggest impact.',
			}),
			img: {
				src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/8i4ut65m3p28wvyedk7505mtqm5kiqr3.webp',
				alt: '',
				width: 768,
				height: 512,
			},
		},
		{
			title: intl.formatMessage({
				id: 'JiraProductDiscoveryPage.scroller.2.title',
				defaultMessage: 'Get everyone on the same page',
			}),
			description: intl.formatMessage({
				id: 'JiraProductDiscoveryPage.scroller.2.description',
				defaultMessage:
					'Spend less time updating and persuading with custom roadmaps that give your stakeholders the information they need.',
			}),
			img: {
				src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/g5xp0826vanhei54fi3437v7nr227cn7.webp',
				alt: '',
				width: 768,
				height: 512,
			},
		},
		{
			title: intl.formatMessage({
				id: 'JiraProductDiscoveryPage.scroller.3.title',
				defaultMessage: 'Connect discovery and delivery in Jira',
			}),
			description: intl.formatMessage({
				id: 'JiraProductDiscoveryPage.scroller.3.description',
				defaultMessage:
					'Reduce context switching, add visibility, and move faster by seamlessly connecting your ideas to the delivery work happening in Jira.',
			}),
			img: {
				src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/767r1t8n744265g8k4550obw1udklx2f.webp',
				alt: '',
				width: 768,
				height: 512,
			},
		},
	];
	return (
		<section css={sectionStyle}>
			<div>
				<Scroller
					items={items}
					renderText={(item) => <ScrollerContent item={item} />}
					renderGraphic={(item) => <Picture image={item.img} css={imgStyle} />}
					layout="right"
					mobileGraphicLayout="above"
					backgroundColor="#fff"
					inScrollBackgroundColor="#fff"
					heading={
						<div css={scrollerHeading}>
							<h2 css={heading}>
								<FormattedMessage
									id="JiraProductDiscoveryPage.scroller.heading.text"
									defaultMessage="Discover your next great product idea"
								/>
							</h2>
							<p>
								<FormattedMessage
									id="JiraProductDiscoveryPage.scroller.subheading.text"
									defaultMessage="Better product decisions are made as a team"
								/>
							</p>
						</div>
					}
				/>
			</div>
		</section>
	);
};

export default SideBySide;

type ScrollerItem = {
	title: string;
	description: string;
	img: {
		src: string;
		alt: string;
		width?: number;
		height?: number;
	};
};

const ScrollerContent = ({ item }: { item: ScrollerItem }) => {
	return (
		<div>
			<h3>{item.title}</h3>
			<p>{item.description}</p>
		</div>
	);
};

const sectionStyle = css({
	overflowX: 'clip',
});
const scrollerHeading = css({
	textAlign: 'center',
	width: '100%',
	display: 'grid',
	paddingBlockEnd: token('space.400'),
});
const heading = css({
	marginBlockEnd: 0,
});
const imgStyle = css({
	maxWidth: '100%',
	// Need to style the underlying img inside the picture element
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		maxWidth: '100%',
		height: 'auto',
	},
});
